import {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import { useSocket } from '../../contexts/Socket';
import {
  firstSighting, getPermission, isAppInForeground, plainTitle, showDesktopNotification,
} from './desktopNotification';

/*
 * The Notification Center's data layer.
 *
 * Replaces the prototype's useApprovalNotifications, which polled the two approval
 * endpoints, could show a manager's queue and nothing else, and kept "read" state in
 * localStorage — per browser rather than per person, and lost on a cache clear.
 *
 * Rows now come from the notifications store, so employees have a feed too, read state
 * is server-owned, and live updates arrive over the socket. The socket is an
 * optimisation: everything it pushes is also readable from the API, so a reconnect
 * refetches rather than trying to replay what was missed.
 */

const PAGE_SIZE = 20;

/** Rows carry a full server-rendered sentence; the UI groups by these instead. */
const UI_CATEGORY = {
  profile_request_submitted: 'profile',
  profile_request_approved: 'profile',
  profile_request_rejected: 'profile',
  pto_request_submitted: 'pto',
  pto_request_approved: 'pto',
  pto_request_denied: 'pto',
  pto_request_auto_approved: 'pto',
  pto_request_edited: 'pto',
  pto_request_deleted: 'pto',
  ack_requested: 'ack',
  ack_submitted: 'ack',
  ack_acknowledged: 'ack',
  hr_bot_alert_self: 'alert',
  hr_bot_alert_subordinate: 'alert',
  schedule_adherence_self: 'alert',
  schedule_adherence_subordinate: 'alert',
};

/** Which icon NotificationRow draws (its TYPE_META keys). */
const uiType = (row) => {
  const category = UI_CATEGORY[row.type];
  if (category === 'profile') return row.payload?.profileField || 'address';
  return category || 'pto';
};

/*
 * The three states a row can show. `pending` is the only one that renders buttons, and
 * it needs the server's actionState: a request approved by somebody else must stop
 * offering Approve/Reject here even though this row was created actionable.
 */
const uiStatus = (row) => {
  if (row.actionable) return row.actionState === 'pending' ? 'pending' : 'actioned';
  if (row.type.endsWith('_approved') || row.type.endsWith('_auto_approved')) return 'approved';
  if (row.type.endsWith('_rejected') || row.type.endsWith('_denied')) return 'rejected';
  return 'info';
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const seconds = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;

  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const ageDays = (iso) => (iso
  ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  : 0);

/**
 * A stored row in the shape the row/panel components already render.
 *
 * The title is passed through as a one-element array because the server renders the
 * whole sentence: the components' fallback composition ("{actor} requested a …") would
 * otherwise rebuild wording that already exists, and drift from it.
 */
const reasonNote = (payload) => {
  const reason = payload.denyReason || payload.reason;
  return reason ? `Reason: ${reason}` : null;
};

/*
 * Where a row leads. The server renders a deep link when the row is written, so it
 * points at the specific request rather than the list it lives in; the category
 * fallbacks are for older rows written before their type had one.
 *
 * Exported because two places navigate: the bell's click handler, and the click on an
 * OS notification. They have to agree, and a second copy would drift.
 */
export const routeFor = (item) => {
  if (item.deepLink) return item.deepLink;
  if (item.category === 'pto') return '/pto/members';
  if (item.category === 'ack' || item.category === 'alert') return '/members/alerts';
  return '/profile-update-requests';
};

export const toItem = (row) => {
  const payload = row.payload || {};
  const status = uiStatus(row);

  return {
    id: String(row.id),
    // What the approve/reject endpoints address — the request, not the notification.
    rawId: row.entityId,
    category: UI_CATEGORY[row.type] || 'profile',
    type: uiType(row),
    actionable: Boolean(row.actionable) && row.actionState === 'pending',
    status,
    title: [row.title],
    actorName: payload.actorName || null,
    dateRange: payload.dateRange || null,
    policy: payload.policyName || null,
    duration: payload.duration || null,
    after: payload.summary || null,
    before: null,
    // body already reads "Sep 1 – Sep 3 · Vacation" for time off, which the chips above
    // render; only show it when it says something they do not.
    note: reasonNote(payload),
    deepLink: row.deepLink || null,
    time: timeAgo(row.createdAt),
    ageDays: ageDays(row.createdAt),
    unread: row.status === 'unread',
    createdAt: row.createdAt,
  };
};

export default function useNotifications() {
  const { member, activeWorkspace } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);
  const { socket } = useSocket();
  const history = useHistory();

  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState({});

  const workspaceId = member?.workspaceId || activeWorkspace?.id;

  // Referenced by the socket handlers, which are bound once per connection.
  const workspaceRef = useRef(workspaceId);
  workspaceRef.current = workspaceId;

  /*
   * Announce a row that has just arrived.
   *
   * Held in a ref and reassigned on render rather than passed as an effect dependency:
   * the socket subscription below is bound once per connection, and threading a
   * callback through its deps would tear down and rebind the listeners on any render
   * that changed one — losing events in the gap for no reason.
   *
   * An OS notification only when the user is looking elsewhere. Interrupting the
   * desktop of someone already watching the bell tick over is noise, and they can see
   * the row arrive. When they are here, or have not granted permission, the in-app
   * toast carries it instead.
   */
  const announceRef = useRef(() => {});
  announceRef.current = (item) => {
    if (!firstSighting(item.id)) return;

    if (!isAppInForeground() && getPermission() === 'granted') {
      // Falls through to the toast when the browser refuses to construct one.
      const shown = showDesktopNotification(item, (clicked) => {
        history.push(routeFor(clicked));
      });
      if (shown) return;
    }

    displayNotification({
      message: plainTitle(item.title) || 'You have a new notification',
      severity: 'info',
    });
  };

  const fetchPage = useCallback(async (cursor = null) => {
    if (!workspaceId) return;
    if (!cursor) setLoading(true);

    const res = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.notifications(workspaceId),
      params: { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) },
    });

    setLoading(false);
    if (!res.success) return;

    const page = (res.data?.notifications || []).map(toItem);

    setItems((prev) => (cursor ? [...prev, ...page] : page));
    setNextCursor(res.data?.nextCursor || null);
    setUnreadCount(res.data?.unreadCount ?? 0);
  }, [workspaceId]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (nextCursor) fetchPage(nextCursor);
  }, [nextCursor, fetchPage]);

  useEffect(() => {
    if (!socket) return undefined;

    const onNew = (row) => {
      const item = toItem(row);

      setItems((prev) => {
        // The same row can arrive twice — a push racing a refetch after reconnect.
        if (prev.some((i) => i.id === item.id)) return prev;
        return [item, ...prev];
      });
      setUnreadCount((n) => n + 1);
      announceRef.current(item);
    };

    // Somebody else actioned the underlying request, or another tab read the row.
    const onUpdated = (patch) => setItems((prev) => prev.map((i) => (i.id === String(patch.id)
      ? {
        ...i,
        unread: patch.status ? patch.status === 'unread' : i.unread,
        actionable: i.actionable && patch.actionState === 'pending',
        status: patch.actionState && patch.actionState !== 'pending' ? 'actioned' : i.status,
      }
      : i)));

    const onCount = ({ unread }) => setUnreadCount(unread ?? 0);

    // Events that arrived while the socket was down were never queued; the store has
    // them, so the cheapest correct recovery is to reload the first page.
    const onReconnect = () => fetchPage();

    socket.on('notification:new', onNew);
    socket.on('notification:updated', onUpdated);
    socket.on('notification:count', onCount);
    socket.io.on('reconnect', onReconnect);

    return () => {
      socket.off('notification:new', onNew);
      socket.off('notification:updated', onUpdated);
      socket.off('notification:count', onCount);
      socket.io.off('reconnect', onReconnect);
    };
  }, [socket, fetchPage]);

  const markAllRead = useCallback(async () => {
    if (!workspaceId) return;

    // Optimistic: the badge should clear on click, not on the round trip.
    setItems((prev) => prev.map((i) => ({ ...i, unread: false })));
    setUnreadCount(0);

    const res = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.notificationsRead(workspaceId),
      data: {},
    });

    if (!res.success) fetchPage();
  }, [workspaceId, fetchPage]);

  const markRead = useCallback(async (item) => {
    if (!workspaceId || !item.unread) return;

    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, unread: false } : i)));
    setUnreadCount((n) => Math.max(0, n - 1));

    await sendRequest({
      method: 'put',
      url: API.WORKSPACE.notificationsRead(workspaceId),
      data: { ids: [item.id] },
    });
  }, [workspaceId]);

  const setBusy = (id, busy) => setBusyIds((prev) => {
    const next = { ...prev };
    if (busy) next[id] = true;
    else delete next[id];
    return next;
  });

  /*
   * Approve/reject still call the endpoints that own the underlying request. The row is
   * a pointer to work, not a second write path — and the server clears the pending
   * state for every other approver who saw it (NotificationService.resolveAction).
   */
  const act = useCallback(async (item, action, reason) => {
    if (!workspaceId || !item.rawId) return;

    setBusy(item.id, true);

    const approving = action === 'approve';
    let res;

    if (item.category === 'pto') {
      res = approving
        ? await sendRequest({
          method: 'put',
          url: API.WORKSPACE.approveOffTimeRequest(workspaceId, item.rawId),
        })
        : await sendRequest({
          method: 'put',
          url: API.WORKSPACE.denyOffTimeRequest(workspaceId, item.rawId),
          data: { denyReason: reason },
        });
    } else {
      res = await sendRequest({
        method: 'put',
        url: API.WORKSPACE.profileUpdateApprovalByManager(item.rawId),
        data: { status: approving ? 'approved' : 'rejected', reason },
      });
    }

    setBusy(item.id, false);

    if (!res.success) {
      displayNotification({
        message: res.data?.error || res.data?.message || 'Could not complete that. Please try again.',
        severity: 'error',
      });
      return;
    }

    setItems((prev) => prev.map((i) => (i.id === item.id
      ? {
        ...i, actionable: false, status: approving ? 'approved' : 'rejected', unread: false,
      }
      : i)));

    const who = item.actorName ? `${item.actorName}'s` : 'the';
    displayNotification({
      message: approving ? `Approved ${who} request` : `Rejected ${who} request`,
      severity: approving ? 'success' : 'error',
    });
  }, [workspaceId, displayNotification]);

  const approve = useCallback((item) => act(item, 'approve'), [act]);
  const reject = useCallback((item, reason) => act(item, 'reject', reason), [act]);

  return useMemo(() => ({
    items,
    loading,
    unreadCount,
    busyIds,
    nextCursor,
    approve,
    reject,
    markRead,
    markAllRead,
    loadMore,
    refetch: fetchPage,
  }), [
    items, loading, unreadCount, busyIds, nextCursor,
    approve, reject, markRead, markAllRead, loadMore, fetchPage,
  ]);
}
